/* Scrolling hotspots — <mw-hotspots> for sections/scrolling-hotspots.liquid.

   Desktop (Helix behaviour, measured 19.09): each hotspot slot fades with its distance from the
   middle of the screen under the header — fully visible within 100 px, gone 500 px away (the
   first never below 0.2, the last stays visible once it has passed the middle). The slot within
   100 px of the middle is active and its pin scales to 1.3; between slots no pin is active.
   Clicking a pin scrolls its slot to the middle (smoothly unless reduced motion).
   Mobile: the panels are a horizontal snap row. The card at the start of the row is active and
   drives its pin; tapping a pin scrolls the row to its card. The first pin starts active.
   Reduced motion: no fading; the active slot is the last one whose top passed the middle.
   Work runs in one animation frame per scroll, only while the section is near the screen. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const mobile = window.matchMedia('(max-width: 767px)');
const FULL = 100; // px from the middle where a slot is fully visible and active
const FADE = 500; // px from the middle where a slot has faded out
const LOCK_MS = 900; // hold a clicked pin while the scroll it started plays out

class MwHotspots extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.hotspots') || this;
    this.row = this.querySelector('[data-row]');
    this.pins = [...this.querySelectorAll('[data-pin]')];
    this.slots = [...this.querySelectorAll('[data-hotspot-panel]')];
    if (!this.row || this.slots.length === 0) return;

    this.active = -2;
    this.frame = 0;
    this.lockUntil = 0;
    this.inView = false;
    this.abort = new AbortController();
    const signal = this.abort.signal;

    this.request = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
    };
    window.addEventListener('scroll', this.request, { passive: true, signal });
    window.addEventListener('resize', this.request, { passive: true, signal });
    this.row.addEventListener('scroll', this.request, { passive: true, signal });
    mobile.addEventListener('change', () => this.reset(), { signal });
    reducedMotion.addEventListener('change', () => this.reset(), { signal });
    this.pins.forEach((pin) => pin.addEventListener('click', (event) => this.onPin(event, pin), { signal }));
    this.addEventListener('shopify:block:select', (event) => this.onEditor(event), { signal });

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.request();
      },
      { rootMargin: '100% 0px' }
    );
    this.observer.observe(this);
    // Pins breathe once the section is actually on screen.
    this.arrival = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.section.classList.add('is-inview');
      this.arrival.disconnect();
    });
    this.arrival.observe(this.row.previousElementSibling || this);

    this.reset();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.arrival?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    clearTimeout(this.unlockTimer);
  }

  get fading() {
    return !mobile.matches && !reducedMotion.matches;
  }

  headerOffset() {
    return parseFloat(getComputedStyle(this).getPropertyValue('--header-offset')) || 0;
  }

  // Middle of the visible area under the header.
  middle() {
    const top = this.headerOffset();
    return top + (window.innerHeight - top) / 2;
  }

  reset() {
    this.active = -2;
    if (!this.fading) this.slots.forEach((slot) => slot.style.removeProperty('--hs-o'));
    if (mobile.matches) this.activate(this.rowIndex());
    this.request();
  }

  update() {
    this.frame = 0;
    if (!this.inView) return;
    const locked = performance.now() < this.lockUntil;
    if (mobile.matches) {
      if (!locked) this.activate(this.rowIndex());
      return;
    }

    const middle = this.middle();
    const last = this.slots.length - 1;
    let best = -1;
    let bestDistance = Infinity;
    let passed = 0;
    this.slots.forEach((slot, i) => {
      const panel = slot.firstElementChild || slot;
      const rect = panel.getBoundingClientRect();
      const centre = rect.top + rect.height / 2;
      const distance = Math.abs(middle - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
      if (rect.top <= middle) passed = i;
      if (!this.fading) return;
      let opacity = distance <= FULL ? 1 : 1 - distance / FADE;
      opacity = Math.min(1, Math.max(i === 0 ? 0.2 : 0, opacity));
      if (i === last && centre <= middle) opacity = 1;
      slot.style.setProperty('--hs-o', opacity.toFixed(3));
    });

    if (locked) return;
    if (!this.fading) this.activate(passed);
    else this.activate(bestDistance <= FULL ? best : -1);
  }

  // Index of the card at the start of the mobile row.
  rowIndex() {
    const start = this.row.getBoundingClientRect().left + parseFloat(getComputedStyle(this.row).paddingLeft || '0');
    let index = 0;
    let bestDistance = Infinity;
    this.slots.forEach((slot, i) => {
      const distance = Math.abs(slot.getBoundingClientRect().left - start);
      if (distance < bestDistance) {
        bestDistance = distance;
        index = i;
      }
    });
    return index;
  }

  activate(index) {
    if (index === this.active) return;
    this.active = index;
    const id = this.slots[index]?.id;
    this.slots.forEach((slot, i) => slot.classList.toggle('is-active', i === index));
    this.pins.forEach((pin) => {
      const on = id !== undefined && pin.dataset.pin === id;
      pin.classList.toggle('is-active', on);
      if (on) pin.setAttribute('aria-current', 'true');
      else pin.removeAttribute('aria-current');
    });
  }

  onPin(event, pin) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const index = this.slots.findIndex((slot) => slot.id === pin.dataset.pin);
    if (index < 0) return;
    event.preventDefault();
    this.show(index, true);
  }

  // Bring slot `index` into view: the row scrolls on mobile, the page on desktop.
  show(index, focus) {
    const slot = this.slots[index];
    const behavior = reducedMotion.matches ? 'auto' : 'smooth';
    this.activate(index);
    this.lock(reducedMotion.matches ? 0 : LOCK_MS);

    if (mobile.matches) {
      const pad = parseFloat(getComputedStyle(this.row).paddingLeft || '0');
      const left = this.row.scrollLeft + slot.getBoundingClientRect().left - this.row.getBoundingClientRect().left - pad;
      this.row.scrollTo({ left, behavior });
    } else {
      const panel = slot.firstElementChild || slot;
      const rect = panel.getBoundingClientRect();
      const top = window.scrollY + rect.top + rect.height / 2 - this.middle();
      window.scrollTo({ top: Math.max(0, top), behavior });
    }
    if (focus) slot.focus({ preventScroll: true });
  }

  // The editor selected a hotspot (or a block inside it): show it and light its pin.
  onEditor(event) {
    const slot = event.target.closest?.('[data-hotspot-panel]');
    const index = this.slots.indexOf(slot);
    if (index >= 0) this.show(index, false);
  }

  lock(ms) {
    this.lockUntil = performance.now() + ms;
    clearTimeout(this.unlockTimer);
    this.unlockTimer = setTimeout(this.request, ms + 50);
  }
}

if (!customElements.get('mw-hotspots')) customElements.define('mw-hotspots', MwHotspots);
